import { BaseEntity, GenericListEntity, UploadFile, AttachedFile, CompanyInfo } from "@base/entities/BaseEntities";
import { QueryList } from "@angular/core";
import cloneDeep from 'lodash/cloneDeep';
import camelcaseKeys from 'camelcase-keys';
import { Subject } from "rxjs";

/** Utility functions for interaction with objects */
// @dynamic
export class Utils {

    /** Returns a new typed array */
    public static MapList<TEntity extends BaseEntity>(type: (new () => TEntity), Entities: TEntity[]): TEntity[] {
        //Empty response, do nothing
        if (Entities.length == 0) return undefined;

        //Map each element and add to array
        var newArray: Array<TEntity> = new Array<TEntity>();
        Entities.forEach(Entity => {
            newArray.push(this.MapObject<TEntity>(type, Entity));
        })

        return newArray;
    }

    public static MapAsNumeric(item: GenericListEntity): any {
        return (
            {
                Value: +item.Value,
                Text: item.Text,
                Description: item.Description
            });
    }

    /** Copy all properties from source object to a new typed object */
    /**
     * 
     * @param type The type of the result entity
     * @param Entity The value of the source 
     */
    public static MapObject<TEntity extends BaseEntity>(type: (new () => TEntity), Entity: TEntity): TEntity {
        //Empty response, do nothing
        if (Entity == undefined) return undefined;

        //Create new instance, copy 
        let newObj: TEntity = this.GetNewInstance(type);
        newObj = this.CloneObject(Entity);
        return newObj;
    }

    public static GetNewInstance<TEntity>(type: (new () => TEntity)): TEntity {
        return new type();
    }

    public static CloneObject(obj: object): any {
        return cloneDeep(obj);
    }

    public static IsPrimitiveType(obj: object) {
        return (obj !== Object(obj));
    }

    public static CopyProperties<TEntity extends BaseEntity>(type: (new () => TEntity), source: TEntity, destination: TEntity) {
        //Empty response, do nothing
        if (source == undefined || destination == undefined) return undefined;
        for (var k in source) destination[k] = source[k];
    }

    public static IsEmpty(obj: any): boolean {
        if (typeof (obj) === 'number' && obj === 0) return false;
        return obj === undefined || obj == null || obj == "";
    }

    public static NZ(obj: any, replace: any = ''): string {
        return (this.IsEmpty(obj) ? replace : obj);
    }

    public static ValEx(s: string): number {
        if (this.IsEmpty(s)) return 0;
        if (typeof s === 'number') return s;
        var num = s.replace(",", "").replace(" ", "");
        return +num;
    }

    //Computer checksum hash (not for cryptography)
    public static ComputeChecksum(value: string): string {
        let h1 = 0xdeadbeef ^ 0, h2 = 0x41c6ce57 ^ 0;
        for (let i = 0, ch; i < value.length; i++) {
            ch = value.charCodeAt(i);
            h1 = Math.imul(h1 ^ ch, 2654435761);
            h2 = Math.imul(h2 ^ ch, 1597334677);
        }
        h1 = Math.imul(h1 ^ h1 >>> 16, 2246822507) ^ Math.imul(h2 ^ h2 >>> 13, 3266489909);
        h2 = Math.imul(h2 ^ h2 >>> 16, 2246822507) ^ Math.imul(h1 ^ h1 >>> 13, 3266489909);
        return (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0')
    }

    //Convert value to bool, considering undefined
    public static ToBoolean(value: boolean): boolean {
        if (value == undefined) return false;
        return value;
    }

    //Convert 64base encoded data into blob
    public static ConvertToBlob(oBase64Data: any, sContentType: string, bIsUseUint16: boolean = false): Blob {
        if (oBase64Data === undefined || oBase64Data === null) return undefined;
        var byteCharacters = atob(oBase64Data);
        var byteArrays = [];

        for (var offset = 0; offset < byteCharacters.length; offset += 512) {
            var slice = byteCharacters.slice(offset, offset + 512);
            var byteNumbers = new Array(slice.length);
            for (var i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            var byteArray = !bIsUseUint16 ? new Uint8Array(byteNumbers) : new Uint16Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        return new Blob(byteArrays, { type: sContentType });
    }

    public static ConvertToBase64(bytesArray: number[]) {
        var binary = '';
        var bytes = new Uint8Array(bytesArray);
        var len = bytes.byteLength;
        for (var i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    public static ConvertToBytesArray(base64Data: string) {
        if (base64Data === undefined || base64Data === null) return undefined;
        const data = base64Data.split(';');
        base64Data = data.length > 1 ? data[1].substring(data[1].indexOf(',') + 1) : base64Data;

        let binary_string = window.atob(base64Data);
        let len = binary_string.length;
        let bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binary_string.charCodeAt(i);
        }
        return Array.from(new Uint8Array(bytes.buffer));
    }

    public static GetImageSource(bytesArray: number[], mimeType: string): string {
        return `data:${mimeType};base64,${this.ConvertToBase64(bytesArray)}`;
    }

    public static ExtractDataFromDataURL(DataURL: string): string {
        return DataURL.split(",")[1];
    }

    public static CombineChild<T>(oItem1: T, oItem2: T): T {
        return oItem1 != undefined ? oItem1 : oItem2;
    }

    public static CombineChildren<T>(list1: QueryList<T>, list2: QueryList<T>): T[] {
        var arr: T[] = [];
        if (list1 != undefined) arr = arr.concat(list1.toArray());
        if (list2 != undefined) arr = arr.concat(list2.toArray());
        return arr;
    }

    public static ConvertCSVToJSONArray(strData, sDelimited: string) {
        var data = [], i = 0, k = 0, header = [];


        // Create a regular expression to parse the CSV values.
        var objPattern = new RegExp(
            (
                // Delimiters.
                "(\\" + sDelimited + "|\\r?\\n|\\r|^)" +

                // Quoted fields.
                "(?:\"([^\"]*(?:\"\"[^\"]*)*)\"|" +

                // Standard fields.
                "([^\"\\" + sDelimited + "\\r\\n]*))"
            ),
            "gi"
        );

        // Create an array to hold our data. Give the array
        // a default empty first row.
        var arrData = [[]];

        // Create an array to hold our individual pattern
        // matching groups.
        var arrMatches = null;


        // Keep looping over the regular expression matches
        // until we can no longer find a match.
        while (arrMatches = objPattern.exec(strData)) {

            // Get the delimiter that was found.
            var strMatchedDelimiter = arrMatches[1];

            // Check to see if the given delimiter has a length
            // (is not the start of string) and if it matches
            // field delimiter. If id does not, then we know
            // that this delimiter is a row delimiter.
            if (strMatchedDelimiter.length && (strMatchedDelimiter != sDelimited)) {
                // Since we have reached a new row of data,
                // add an empty row to our data array.
                arrData.push([]);
            }

            // Now that we have our delimiter out of the way,
            // let's check to see which kind of value we
            // captured (quoted or unquoted).
            if (arrMatches[2]) {
                // We found a quoted value. When we capture
                // this value, unescape any double quotes.
                var strMatchedValue = arrMatches[2].replace(new RegExp("\"\"", "g"), "\"");

            }
            else {
                // We found a non-quoted value.
                var strMatchedValue = arrMatches[3];
            }

            // Now that we have our value string, let's add
            // it to the data array.
            arrData[arrData.length - 1].push(strMatchedValue);
        }


        //Filter out empty lines
        arrData = arrData.filter(x => !this.IsEmpty(x));

        //Rename headers
        for (i = 0; i < arrData.length; i++) {
            if (i == 0) {
                header = arrData[i];
            }
            else {
                var obj = {};
                for (k = 0; k < header.length; k++) {
                    if (k < arrData[i].length) {
                        obj[header[k]] = arrData[i][k];
                    }
                }
                data.push(obj);
            }
        }
        return data;

    }

    public static ContainsHebrewLetters(str: any): boolean {
        var regex = new RegExp('[א-ת]+');
        return (regex.test(str));
    }

    public static LoadFilesBody(files: File[], done: (uploads: UploadFile[]) => void) {
        let result: UploadFile[] = [];

        if (files && files.length) {
            const length = files.length;
            let count = 0;

            for (let file of files) {
                let fileReader = new FileReader();

                fileReader.onloadend = (e) => {
                    count++;

                    let buffer = fileReader.result as ArrayBuffer;
                    let array = Array.from(new Uint8Array(buffer));

                    result.push({
                        Name: file.name,
                        Type: file.type,
                        Data: array,
                        Buffer: buffer,
                        File: file
                    } as UploadFile);

                    if (length === count) {
                        done(result);
                    }

                };
                fileReader.readAsArrayBuffer(file);
            };
        }
        else {
            done(result);
        }
    }

    public static DoFileDownload(file: AttachedFile, fileBlob: string, contentType: string, bIsUseUint16: boolean = false) {
        let blob = Utils.ConvertToBlob(fileBlob, contentType, bIsUseUint16);
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        a.href = objectUrl;
        a.download = file.Name + (file.Extension ? '.' + file.Extension : '');
        a.click();
        URL.revokeObjectURL(objectUrl);
    }

    // Compare not undefined/null objects
    public static IsEquals(obj1: {}, obj2: {}, ignoreEmptyProps: boolean = false) {
        if (obj1 === null || obj2 === null || !this.IsObject(obj1) || !this.IsObject(obj2)) return false;
        return this.StringifyObject(obj1, ignoreEmptyProps) === this.StringifyObject(obj2, ignoreEmptyProps);
    }

    public static ConvertBlobToImage(image: Blob, result: (image) => void) {
        const reader = new FileReader();
        reader.addEventListener('load', () => {
            result(reader.result);
        }, false);

        if (image && image.size !== 0) {
            reader.readAsDataURL(image);
        } else {
            result(null);
        }
    }

    public static ConvertBlobToBytesArray(file: Blob, result: (bytes) => void) {
        const reader = new FileReader();
        reader.addEventListener('load', () => {
            let arrayBuffer = (reader.result as ArrayBuffer);
            result(Array.from(new Uint8Array(arrayBuffer)));
        }, false);

        if (file && file.size !== 0) {
            reader.readAsArrayBuffer(file);
        } else {
            result(null);
        }
    }

    public static UniqueBy(array: any[], mapFunc: Function = x => x) {
        return new Set(array.map(x => mapFunc(x)));
    }

    public static LoadJsonFile<T>(path: string): T {
        const xmlhttp = new XMLHttpRequest();
        xmlhttp.open('GET', path, false);
        if (xmlhttp.overrideMimeType) {
            xmlhttp.overrideMimeType('application/json');
        }
        xmlhttp.send();
        const json = xmlhttp.status == 200 && !xmlhttp.responseText.startsWith('<') ? xmlhttp.responseText : null;
        return json ? JSON.parse(json) : null;
    }

    // current implementation doesn't allow to have routes with numbers inside
    //public static getClearURLFromRoute(event) {
    //    const route = event.urlAfterRedirects.split('?')[0]
    //        .replace(/[0-9]/g, '')
    //        .replace(/\/$/, '')
    //        .replace('(', '')
    //        .replace(')', '');
    //    return route.substring(0, route.indexOf('_') - 1) + route.substring(route.lastIndexOf('_') + 1);
    //}

    public static ToCamelCase<T extends Record<string, any> | readonly any[]>(input: T): T {
        return camelcaseKeys<T>(input, { deep: true }) as T;
    }

    public static ParseJSONString(jsonString: string): any {
        return JSON.parse(jsonString, this.dateParser);
    }

    private static _reISO = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*))(?:Z|(\+|-)([\d|:]*))?$/;
    private static _reMsAjax = /^\/Date\((d|-|.*)\)[\/|\\]$/;
    private static dateParser = (key, value) => {
        if (typeof value === 'string') {
            var a = Utils._reISO.exec(value);
            if (a)
                return new Date(value);
            a = Utils._reMsAjax.exec(value);
            if (a) {
                var b = a[1].split(/[-+,.]/);
                return new Date(b[0] ? +b[0] : 0 - +b[1]);
            }
        }
        return value;
    }

    public static StringifyObject(obj: any, ignoreEmptyProps: boolean = false) {
        const copyObj = { ...obj };

        //remove empty props
        if (ignoreEmptyProps) {
            for (var propName in copyObj) {
                if (copyObj[propName] === null || copyObj[propName] === undefined || copyObj[propName] === "") {
                    delete copyObj[propName];
                }
            }
        }
        return JSON.stringify(copyObj)
    }

    public static DeepProxy(obj: any, subject: Subject<void> = null) {
        const self = this;
        const mapStore = {};
        return new Proxy(obj, {
            get(target, property) {
                const value = target[property];
                // if this property has been proxied, just return
                if (mapStore[property] === true) return value;
                // if it's an non-proxied object, we return its proxy
                if (value && Utils.IsObject(value) && !(value instanceof Date) || Utils.IsArray(value)) {
                    const proxyValue = mapStore[property] || self.DeepProxy(value, subject);
                    mapStore[property] = proxyValue;
                    return proxyValue;
                }
                // else we just take a mark
                mapStore[property] = true;
                return value;
            },
            set(target, property, value) {
                const newVal = (Utils.IsObject(value) && value && !(value instanceof Date) || Utils.IsArray(value))
                    ? self.DeepProxy(value, subject)
                    : value;
                target[property] = newVal;
                mapStore[property] = true;
                subject && subject.next();
                return true;
            },
            deleteProperty(target, property) {
                delete target[property];
                delete mapStore[property];
                subject && subject.next();
                return true;
            }
        });
    }

    public static IsArray(arr: any) {
        return Array.isArray(arr);
    }

    public static IsObject(obj: any) {
        return typeof obj === 'object';
    }

    public static IsEmail(email: string, isSupportMultiple: boolean = false) {
        if (!email || email.length === 0) { return false; }

        const validRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        let emails = [];

        if (isSupportMultiple) {
            emails = email.split(';');
        } else {
            emails.push(email);
        }

        for (let i = 0; i < emails.length; i++) {
            if (!emails[i].match(validRegex)) { return false; }
        }

        return true;
    }

    public static GetIconStyle(extension: string) {
        if (extension) {
            switch (extension) {
                case '.pdf':
                    return 'mdi mdi-file-pdf-box';
                case '.doc':
                case '.docx':
                    return 'mdi mdi-file-word-box';
                case '.xls':
                case '.xlsx':
                    return 'mdi mdi-file-excel-box';
                case '.rft':
                case '.txt':
                    return 'mdi mdi-text-box';
                case '.zip':
                case '.rar':
                case '.gzip':
                    return 'mdi mdi-zip-box';
                case '.htm':
                case '.html':
                    return 'mdi mdi-language-html5';
                case '.jpg':
                case '.jpeg':
                    return 'mdi mdi-jpg-box';
                case '.gif':
                    return 'mdi mdi-gif-box';
                case '.bmp':
                case '.tiff':
                case '.png':
                    return 'mdi mdi-file-png-box';
                case '.xml':
                    return 'mdi mdi-file-xml-box';
                default:
                    return 'mdi mdi-file';
            }
        }

        return 'mdi mdi-file';
    }
}
